import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  VStack,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { PortfolioCovenantVariableGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  RecurringDateConfigView,
  RelativeRecurringDateConfigView,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/RecurringDateConfigView';
import {
  PortfolioCovenantQueryInputType,
  queryInputTypeToIcon,
} from 'packages/settings/ui/AddQueryInputModal';

export const PortfolioCovenantPreviewResultsRecurringDatesAccordion = ({
  recurringDatesResults,
  creditAgreementPortfolioCovenant,
  portfolioCovenantFullPropertyKey,
}: {
  recurringDatesResults: {
    [key: string]: PortfolioCovenantVariableGQLType[];
  };
  creditAgreementPortfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  portfolioCovenantFullPropertyKey: string;
}) => {
  return (
    <Accordion allowMultiple>
      <AccordionItem
        backgroundColor="white"
        borderRadius="8px"
        border={`1px solid ${colors.gray[200]}`}
        p="24px"
      >
        <AccordionButton>
          <Box
            as="span"
            flex="1"
            textAlign="left"
            fontSize="20px"
            fontWeight={600}
          >
            Recurring Dates
          </Box>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel>
          <VStack spacing="32px">
            {_.map(
              recurringDatesResults,
              (recurringDateResultGroup, recurringDateConfigKey) => {
                const recurringDateConfigInCreditAgreement = _.find(
                  creditAgreementPortfolioCovenant._config_
                    ?.globalRecurringDateConfigs,
                  (recurringDateConfig) =>
                    recurringDateConfig.field === recurringDateConfigKey,
                );

                return (
                  <Box>
                    <Text mt="16px" mb="24px" fontWeight={500}>
                      {recurringDateConfigKey}
                    </Text>
                    {recurringDateConfigInCreditAgreement?.setup.type ===
                      'static' && (
                      <RecurringDateConfigView
                        recurringDateConfig={
                          recurringDateConfigInCreditAgreement.setup.config
                        }
                        fullPropertyKey={`${portfolioCovenantFullPropertyKey}._config_.globalRecurringDateConfigs.setup.config`}
                        displayName="Recurring Dates"
                        displayStyle="humanReadableString"
                        enableEdit={false}
                      />
                    )}
                    {recurringDateConfigInCreditAgreement?.setup.type ===
                      'relative' && (
                      <RelativeRecurringDateConfigView
                        relativeConfig={
                          recurringDateConfigInCreditAgreement.setup.config
                        }
                        fullPropertyKey={`${portfolioCovenantFullPropertyKey}._config_.globalRecurringDateConfigs.setup.config`}
                        displayName="Recurring Dates"
                        displayStyle="humanReadableString"
                        enableEdit={false}
                      />
                    )}
                    <Table mt="24px" borderWidth="1px">
                      <Thead>
                        <Tr>
                          <Th w="50%">Key</Th>
                          <Th w="50%">Value</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {_.map(
                          recurringDateResultGroup,
                          (recurringDateResult) => (
                            <Tr>
                              <Td>{recurringDateResult.name}</Td>
                              <Td>
                                {_.isNil(recurringDateResult.value)
                                  ? '-'
                                  : recurringDateResult.value}
                              </Td>
                            </Tr>
                          ),
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                );
              },
            )}
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const getQueryType = ({
  inputKey,
  creditAgreementPortfolioCovenant,
}: {
  inputKey: string;
  creditAgreementPortfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
}): PortfolioCovenantQueryInputType | null => {
  const isAthenaQuery = _.find(
    creditAgreementPortfolioCovenant._config_?.athenaQueries,
    (athenaQuery) => athenaQuery.query.name === inputKey,
  );
  if (isAthenaQuery) {
    return 'singleQuery';
  }

  const isAthenaRecurringQuery = _.find(
    creditAgreementPortfolioCovenant._config_?.athenaRecurringQueries,
    (athenaRecurringQuery) => athenaRecurringQuery.name === inputKey,
  );
  if (isAthenaRecurringQuery) {
    return 'multiQuery';
  }

  const postgresQuery = _.find(
    creditAgreementPortfolioCovenant._config_?.postgresQueries,
    (pgQuery) => pgQuery.name === inputKey,
  );
  if (postgresQuery) {
    return postgresQuery.type;
  }

  return null;
};

export const PortfolioCovenantPreviewResultsInputsAccordion = ({
  inputResults,
  creditAgreementPortfolioCovenant,
}: {
  inputResults: {
    [key: string]: PortfolioCovenantVariableGQLType[];
  };
  creditAgreementPortfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
}) => {
  return (
    <VStack spacing="32px" mt="32px">
      {_.map(inputResults, (inputResultGroup, inputKey) => {
        const queryType = getQueryType({
          inputKey,
          creditAgreementPortfolioCovenant,
        });

        const orderedInputResultGroup = _.orderBy(inputResultGroup, 'name');

        return (
          <Accordion allowMultiple width="100%">
            <AccordionItem
              backgroundColor="white"
              borderRadius="8px"
              border={`1px solid ${colors.gray[200]}`}
              p="24px"
            >
              <AccordionButton>
                <Flex fontSize="20px" fontWeight={600} alignItems="center">
                  {queryType && (
                    <Icon
                      icon={queryInputTypeToIcon[queryType].icon}
                      style={{ marginRight: '16px' }}
                    />
                  )}
                  <Text>{inputKey}</Text>
                </Flex>
                <Spacer />
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel>
                <Table mt="24px" borderWidth="1px">
                  <Thead>
                    <Tr>
                      <Th w="50%">Key</Th>
                      <Th w="50%">Value</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {_.map(orderedInputResultGroup, (inputResult) => (
                      <Tr>
                        <Td>{inputResult.name}</Td>
                        <Td>
                          {_.isNil(inputResult.value) ? '-' : inputResult.value}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      })}
    </VStack>
  );
};
